// The one definition of Patient Records' tabs — the strip, the More menu and
// PatientRecords.tsx's panels all read it. Same "critical in the strip, the
// rest behind More" model as the IP Case Sheet (lib/priorityTabs.ts), so the
// record never needs a sideways scroll to reach a tab on a tablet or phone.
//
// Critical = what a clinician needs before touching the patient (latest
// vitals/allergies, current medicines, problem list, trends, results, files).
// Secondary = administrative or archival views (bills, visit workflow log,
// prescription documents, filed consultation text, audit trail).

import type { PriorityTab } from '../../lib/priorityTabs';

export type PatientRecordTabId =
  | 'overview' | 'medications' | 'medical' | 'timeline' | 'vitals' | 'lab' | 'docs'
  | 'prescriptions' | 'visits' | 'admissions' | 'history' | 'billing' | 'audit';

export const PATIENT_RECORD_TABS: readonly PriorityTab<PatientRecordTabId>[] = [
  { id: 'overview', label: 'Overview', group: 'critical', priority: 1 },
  // Current and past medicines (patient_medications) — promoted out of
  // Medical history so a doctor reaches it in one tap.
  { id: 'medications', label: 'Medications', group: 'critical', priority: 2 },
  // Diagnoses (the editable problem list) and past procedures.
  { id: 'medical', label: 'Medical history', group: 'critical', priority: 3 },
  // AI-built chronological milestones across every record — a different thing
  // from the problem list above, which it only helps seed.
  { id: 'timeline', label: 'Clinical timeline', group: 'critical', priority: 7 },
  { id: 'vitals', label: 'Vitals trend', group: 'critical', priority: 4 },
  { id: 'lab', label: 'Laboratory', group: 'critical', priority: 5, module: 'laboratory' },
  { id: 'docs', label: 'Documents', group: 'critical', priority: 6 },
  { id: 'prescriptions', label: 'Prescriptions', group: 'secondary' },
  { id: 'visits', label: 'Visits', group: 'secondary' },
  // In-patient stays at this branch, open and discharged, with their Case
  // Sheet. The way back to a discharged admission's record.
  { id: 'admissions', label: 'Admissions', group: 'secondary', module: 'ip' },
  { id: 'history', label: 'Consultation history', group: 'secondary' },
  { id: 'billing', label: 'Billing', group: 'secondary' },
  { id: 'audit', label: 'Audit trail', group: 'secondary', module: 'records:audit' },
];

/** Tabs this user may see — a gated tab is absent, not disabled. The backend
 * re-checks each grant; hiding the tab only avoids offering a 403. */
export function patientRecordTabs(can: (moduleId: string) => boolean): PriorityTab<PatientRecordTabId>[] {
  return PATIENT_RECORD_TABS.filter(t => !t.module || can(t.module));
}
